import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Badge } from './ui/badge';
import { Button } from './ui/button';
import { Skeleton } from './ui/skeleton';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import {
  Brain,
  TrendingUp,
  TrendingDown,
  Target,
  Users,
  BarChart3,
  Activity,
  RefreshCw,
  Zap,
  Award,
  AlertTriangle,
  CheckCircle,
  XCircle,
  Minus
} from 'lucide-react';
import { toast } from 'sonner';
import {
  LineChart,
  Line,
  AreaChart,
  Area,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Legend,
  ComposedChart
} from 'recharts';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function AILearningDashboard() {
  const [loading, setLoading] = useState(true);
  const [dashboardData, setDashboardData] = useState(null);
  const [accuracyHistory, setAccuracyHistory] = useState([]);
  const [selectedLeague, setSelectedLeague] = useState('all');
  const [historyDays, setHistoryDays] = useState(30);

  const getAuthHeaders = () => ({
    'Authorization': `Bearer ${localStorage.getItem('token')}`,
    'Content-Type': 'application/json'
  });

  const fetchDashboardData = async () => {
    setLoading(true);
    try {
      const [dashRes, historyRes] = await Promise.all([
        fetch(`${API_URL}/api/ai-learning/dashboard`, { headers: getAuthHeaders() }),
        fetch(`${API_URL}/api/ai-learning/accuracy-history?days=${historyDays}${selectedLeague !== 'all' ? `&league_id=${selectedLeague}` : ''}`, { headers: getAuthHeaders() })
      ]);

      if (dashRes.ok) {
        const data = await dashRes.json();
        setDashboardData(data);
      }
      if (historyRes.ok) {
        const data = await historyRes.json();
        setAccuracyHistory(data.chart_data || []);
      }
    } catch (err) {
      console.error('Failed to fetch AI Learning dashboard:', err);
      toast.error('Failed to load AI Learning data');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchDashboardData();
  }, [selectedLeague, historyDays]);

  if (loading) {
    return (
      <div className="space-y-6">
        <Skeleton className="h-48 w-full" />
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <Skeleton className="h-32" />
          <Skeleton className="h-32" />
          <Skeleton className="h-32" />
        </div>
        <Skeleton className="h-64 w-full" />
      </div>
    );
  }

  const overall = dashboardData?.overall || {};
  const leagueAccuracy = dashboardData?.league_accuracy || {};
  const factorEffectiveness = dashboardData?.factor_effectiveness || [];
  const topPlayers = dashboardData?.top_impact_players || [];

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="p-2 bg-purple-500/20 rounded-lg">
            <Brain className="w-6 h-6 text-purple-500" />
          </div>
          <div>
            <h2 className="text-xl font-bold">AI Learning Dashboard</h2>
            <p className="text-sm text-muted-foreground">Track accuracy trends, factor effectiveness, and player impact</p>
          </div>
        </div>
        <div className="flex items-center gap-3">
          <Select value={selectedLeague} onValueChange={setSelectedLeague}>
            <SelectTrigger className="w-32">
              <SelectValue placeholder="League" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Leagues</SelectItem>
              <SelectItem value="nba">NBA</SelectItem>
              <SelectItem value="nhl">NHL</SelectItem>
              <SelectItem value="mlb">MLB</SelectItem>
              <SelectItem value="nfl">NFL</SelectItem>
              <SelectItem value="mls">MLS</SelectItem>
              <SelectItem value="epl">EPL</SelectItem>
            </SelectContent>
          </Select>
          <Button variant="outline" size="icon" onClick={fetchDashboardData}>
            <RefreshCw className="w-4 h-4" />
          </Button>
        </div>
      </div>

      {/* Overview Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card className="bg-gradient-to-br from-purple-500/10 to-purple-500/5 border-purple-500/20">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Total Analyzed</p>
                <p className="text-3xl font-bold text-purple-500">{overall.total_predictions_analyzed || 0}</p>
              </div>
              <Activity className="w-8 h-8 text-purple-500/50" />
            </div>
          </CardContent>
        </Card>

        <Card className={`bg-gradient-to-br ${overall.overall_accuracy >= 60 ? 'from-green-500/10 to-green-500/5 border-green-500/20' : 'from-yellow-500/10 to-yellow-500/5 border-yellow-500/20'}`}>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Overall Accuracy</p>
                <p className={`text-3xl font-bold ${overall.overall_accuracy >= 60 ? 'text-green-500' : 'text-yellow-500'}`}>
                  {overall.overall_accuracy || 0}%
                </p>
              </div>
              <Target className="w-8 h-8 text-green-500/50" />
            </div>
          </CardContent>
        </Card>

        <Card className="bg-gradient-to-br from-blue-500/10 to-blue-500/5 border-blue-500/20">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Teams Tracked</p>
                <p className="text-3xl font-bold text-blue-500">{overall.teams_tracked || 0}</p>
              </div>
              <Users className="w-8 h-8 text-blue-500/50" />
            </div>
          </CardContent>
        </Card>

        <Card className="bg-gradient-to-br from-orange-500/10 to-orange-500/5 border-orange-500/20">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Players Tracked</p>
                <p className="text-3xl font-bold text-orange-500">{overall.players_tracked || 0}</p>
              </div>
              <Zap className="w-8 h-8 text-orange-500/50" />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Accuracy Trend Chart */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="flex items-center gap-2">
                <BarChart3 className="w-5 h-5" />
                Accuracy Trend
              </CardTitle>
              <CardDescription>Daily prediction accuracy over time</CardDescription>
            </div>
            <Select value={String(historyDays)} onValueChange={(v) => setHistoryDays(Number(v))}>
              <SelectTrigger className="w-24">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="7">7 days</SelectItem>
                <SelectItem value="14">14 days</SelectItem>
                <SelectItem value="30">30 days</SelectItem>
                <SelectItem value="60">60 days</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </CardHeader>
        <CardContent>
          <div className="h-64">
            <ResponsiveContainer width="100%" height="100%">
              <ComposedChart data={accuracyHistory}>
                <CartesianGrid strokeDasharray="3 3" stroke="#333" />
                <XAxis dataKey="date" tick={{ fontSize: 10 }} stroke="#666" />
                <YAxis yAxisId="left" tick={{ fontSize: 10 }} stroke="#666" domain={[0, 100]} />
                <YAxis yAxisId="right" orientation="right" tick={{ fontSize: 10 }} stroke="#666" />
                <Tooltip
                  contentStyle={{ backgroundColor: '#1a1a1a', border: '1px solid #333' }}
                  formatter={(value, name) => {
                    if (name === 'accuracy') return [`${value}%`, 'Accuracy'];
                    return [value, name.charAt(0).toUpperCase() + name.slice(1)];
                  }}
                />
                <Legend />
                <Bar yAxisId="right" dataKey="predictions" fill="#3b82f6" name="Predictions" radius={[2, 2, 0, 0]} opacity={0.7} />
                <Line yAxisId="left" type="monotone" dataKey="accuracy" stroke="#22c55e" strokeWidth={2} name="Accuracy %" dot={{ fill: '#22c55e', r: 4 }} />
              </ComposedChart>
            </ResponsiveContainer>
          </div>
        </CardContent>
      </Card>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* League Accuracy Breakdown */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Target className="w-5 h-5" />
              Accuracy by League
            </CardTitle>
            <CardDescription>Detailed breakdown per league</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {Object.entries(leagueAccuracy).sort((a, b) => b[1].accuracy - a[1].accuracy).map(([league, data]) => (
                <div key={league} className="flex items-center justify-between p-3 bg-secondary/30 rounded-lg">
                  <div className="flex items-center gap-3">
                    <Badge variant="outline" className="uppercase font-bold">
                      {league}
                    </Badge>
                    <span className="text-sm text-muted-foreground">
                      {data.correct}/{data.total} correct
                    </span>
                  </div>
                  <div className="flex items-center gap-2">
                    <div className="w-24 h-2 bg-secondary rounded-full overflow-hidden">
                      <div
                        className={`h-full ${data.accuracy >= 60 ? 'bg-green-500' : data.accuracy >= 50 ? 'bg-yellow-500' : 'bg-red-500'}`}
                        style={{ width: `${data.accuracy}%` }}
                      />
                    </div>
                    <span className={`font-bold ${data.accuracy >= 60 ? 'text-green-500' : data.accuracy >= 50 ? 'text-yellow-500' : 'text-red-500'}`}>
                      {data.accuracy}%
                    </span>
                    {data.accuracy >= 65 && <Badge className="bg-green-500">HOT</Badge>}
                  </div>
                </div>
              ))}
              {Object.keys(leagueAccuracy).length === 0 && (
                <p className="text-center text-muted-foreground py-8">No league data available yet</p>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Factor Effectiveness */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Zap className="w-5 h-5" />
              Factor Effectiveness
            </CardTitle>
            <CardDescription>How well each prediction factor performs</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {factorEffectiveness.slice(0, 8).map((factor, idx) => (
                <div key={factor.factor} className="flex items-center justify-between p-3 bg-secondary/30 rounded-lg">
                  <div className="flex items-center gap-3">
                    <span className={`w-6 h-6 flex items-center justify-center rounded text-xs font-bold ${
                      idx === 0 ? 'bg-yellow-500 text-black' :
                      idx === 1 ? 'bg-gray-400 text-black' :
                      idx === 2 ? 'bg-amber-600 text-white' :
                      'bg-secondary text-foreground'
                    }`}>
                      {idx + 1}
                    </span>
                    <div>
                      <p className="font-medium capitalize">{factor.factor.replace(/_/g, ' ')}</p>
                      <p className="text-xs text-muted-foreground">Used {factor.times_used} times</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <Badge variant={
                      factor.effectiveness === 'high' ? 'default' :
                      factor.effectiveness === 'medium' ? 'secondary' : 'destructive'
                    }>
                      {Number((factor.accuracy || 0) * 100).toFixed(0)}%
                    </Badge>
                    {factor.effectiveness === 'high' && <CheckCircle className="w-4 h-4 text-green-500" />}
                    {factor.effectiveness === 'low' && <AlertTriangle className="w-4 h-4 text-red-500" />}
                  </div>
                </div>
              ))}
              {factorEffectiveness.length === 0 && (
                <p className="text-center text-muted-foreground py-8">No factor data available yet</p>
              )}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Top Impact Players */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Award className="w-5 h-5 text-yellow-500" />
            Top Impact Players
          </CardTitle>
          <CardDescription>Players with highest impact on team performance (based on AI learning)</CardDescription>
        </CardHeader>
        <CardContent>
          {topPlayers.length > 0 ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {topPlayers.map((player, idx) => (
                <div key={player.player_id} className={`p-4 rounded-lg border ${
                  idx === 0 ? 'bg-yellow-500/10 border-yellow-500/30' :
                  idx === 1 ? 'bg-gray-400/10 border-gray-400/30' :
                  idx === 2 ? 'bg-amber-600/10 border-amber-600/30' :
                  'bg-secondary/30 border-border'
                }`}>
                  <div className="flex items-center justify-between mb-2">
                    <div className="flex items-center gap-2">
                      <span className={`w-6 h-6 flex items-center justify-center rounded text-xs font-bold ${
                        idx === 0 ? 'bg-yellow-500 text-black' :
                        idx === 1 ? 'bg-gray-400 text-black' :
                        idx === 2 ? 'bg-amber-600 text-white' :
                        'bg-secondary text-foreground'
                      }`}>
                        {idx + 1}
                      </span>
                      <div>
                        <p className="font-medium">{player.player_name}</p>
                        <p className="text-xs text-muted-foreground uppercase">{player.league_id} • {player.position}</p>
                      </div>
                    </div>
                    <div className="text-right">
                      <p className={`text-lg font-bold ${player.impact_differential > 0 ? 'text-green-500' : 'text-red-500'}`}>
                        {player.impact_differential > 0 ? '+' : ''}{player.impact_differential}%
                      </p>
                      <p className="text-xs text-muted-foreground">Impact</p>
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-2 text-xs">
                    <div className="bg-green-500/10 p-2 rounded">
                      <p className="text-muted-foreground">Win% Playing</p>
                      <p className="font-bold text-green-500">{player.win_rate_playing}%</p>
                    </div>
                    <div className="bg-red-500/10 p-2 rounded">
                      <p className="text-muted-foreground">Win% Absent</p>
                      <p className="font-bold text-red-500">{player.win_rate_absent}%</p>
                    </div>
                  </div>
                  <p className="text-xs text-muted-foreground mt-2 text-center">
                    {player.games_tracked} games tracked
                  </p>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-12 text-muted-foreground">
              <Users className="w-12 h-12 mx-auto mb-4 opacity-50" />
              <p>No player impact data available yet</p>
              <p className="text-sm mt-2">Player impact is tracked as games are processed by the AI learning system</p>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
